import { useEffect, RefObject } from "react";

interface UseScrollToFragmentOptions {
  isExpanded: boolean;
  contentRef: RefObject<HTMLElement>;
  onExpand: () => void;
  animationDuration?: number;
}

/**
 * Hook to handle automatic expansion and scrolling when URL fragment points to content
 * inside collapsed accordion or expandable components
 */
export const useScrollToFragment = ({
  isExpanded,
  contentRef,
  onExpand,
  animationDuration = 400,
}: UseScrollToFragmentOptions) => {
  useEffect(() => {
    const handleFragmentScroll = () => {
      const hash = window.location.hash;
      if (!hash || isExpanded) return;

      const targetElement = document.querySelector(hash);
      if (targetElement && contentRef.current?.contains(targetElement)) {
        onExpand();
        // Smooth scroll to element after expansion
        setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, animationDuration + 50); // Wait for animation to complete plus small buffer
      }
    };

    // Check on mount and when hash changes
    handleFragmentScroll();
    window.addEventListener("hashchange", handleFragmentScroll);

    return () => window.removeEventListener("hashchange", handleFragmentScroll);
  }, [isExpanded, contentRef, onExpand, animationDuration]);
};
